// Kundenerstinformation für alle im Haushalt bzw. im Verbund (User 28.09.2026:
// „neben Herunterladen auch für alle im Verbund und für alle im Haushalt
// generieren — nur zeigen, wenn es Verbund oder Haushalt gibt, in der Akte und
// in der Anlage"; Nachtrag: „je Kontakt eine PDF generieren, herunterladen und
// bei dem Kontakt hinterlegen"; Verbund = „Firmen-Verbindung zu" bei einer
// Firma + „Verbindung zu Unternehmen" bei einer Person).
//
// Je Kontakt läuft derselbe Weg wie beim einzelnen „Herunterladen", also auch
// mit dessen Rechten:
//  - Bestehender Kontakt: Erstinformation anlegen (liegt dann in SEINER Akte
//    unter Vollmachten/Kundenerstinformation) und seine PDF herunterladen.
//    Hat er schon eine hochgeladene oder unterschriebene Fassung, wird er
//    übersprungen. Ohne Schreibrecht an ihm gibt es nur die Vorschau, ohne Ablage.
//  - Noch nicht gespeicherter Kontakt (Anlage: der neue Kontakt selbst und frei
//    eingetippte Mitglieder): nur die Vorschau. Die des neuen Kontakts legt das
//    Speichern ohnehin ab.

import { VOLLMACHT_STANDARD_KEY } from "@kmvs/forms-core";
import { blobHerunterladen, VollmachtFehler, vollmachtService } from "../../services/vollmachtService";
import type { Haushalt } from "../redesign/table/haushalt";
import type { Unternehmensverbund } from "../redesign/table/unternehmensverbund";
import {
  refDisplayName,
  refHasName,
  type FirmaConnItem,
  type HouseholdItem,
  type PersonFirmaItem,
} from "../relationsInline/types";
import type { VollmachtDto, VollmachtVorschauDaten } from "./vollmachtTypes";

export type SammelArt = "haushalt" | "verbund";

export const SAMMEL_LABEL: Record<SammelArt, string> = {
  haushalt: "Für alle im Haushalt generieren",
  verbund: "Für alle im Verbund generieren",
};

/** Ein Kontakt: bestehend (`contactId`) oder noch nicht gespeichert (nur in der Anlage). */
export type SammelMitglied = { contactId?: number; name: string; anschrift?: string; firma?: string };

// ── Wer gehört dazu ────────────────────────────────────────────────────

type IdName = { id: number; name: string | null | undefined };

function ohneSelbstUndDoppelte(selbst: number, liste: IdName[]): SammelMitglied[] {
  const gesehen = new Set<number>([selbst]);
  const aus: SammelMitglied[] = [];
  for (const k of liste) {
    if (!k?.id || gesehen.has(k.id)) continue;
    gesehen.add(k.id);
    aus.push({ contactId: k.id, name: k.name?.trim() || `Kontakt ${k.id}` });
  }
  return aus;
}

/**
 * Akte. Haushalt: die Mitglieder ohne Verstorbene. Verbund: bei einer Firma die
 * Verbundfirmen, bei einer Person ihre Unternehmen („Verbindung zu
 * Unternehmen", aus der Akte als `unternehmen` hereingereicht).
 */
export function mitgliederAusUmfeld(
  selbst: number,
  art: SammelArt,
  haushalt: Pick<Haushalt, "mitglieder"> | null,
  verbund: Pick<Unternehmensverbund, "firmen"> | null,
  unternehmen: IdName[] = [],
): SammelMitglied[] {
  if (art === "haushalt") {
    return ohneSelbstUndDoppelte(selbst, (haushalt?.mitglieder ?? []).filter((m) => !m.verstorben));
  }
  return ohneSelbstUndDoppelte(selbst, [...(verbund?.firmen ?? []), ...unternehmen]);
}

const anschriftAusRef = (ref: { street?: string; postalCode?: string; city?: string }): string =>
  [ref.street?.trim(), [ref.postalCode?.trim(), ref.city?.trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");

function ausRefs(items: Array<{ ref: HouseholdItem["ref"] }>, ersatzAnschrift: string, firma: boolean): SammelMitglied[] {
  const gesehen = new Set<string>();
  const aus: SammelMitglied[] = [];
  for (const it of items) {
    const ref = it.ref;
    if (!ref || !refHasName(ref) || ref.deceased) continue;
    const name = refDisplayName(ref);
    const schluessel = ref.contactId ? `id:${ref.contactId}` : `neu:${name.toLowerCase()}`;
    if (gesehen.has(schluessel)) continue;
    gesehen.add(schluessel);
    if (ref.contactId) {
      aus.push({ contactId: ref.contactId, name });
    } else {
      const anschrift = anschriftAusRef(ref) || ersatzAnschrift;
      aus.push({ name, ...(anschrift ? { anschrift } : {}), ...(firma ? { firma: name } : {}) });
    }
  }
  return aus;
}

/**
 * Anlage (Privat): die Personen im Haushalt. Wer im selben Haushalt lebt und
 * noch keine eigene Anschrift hat, bekommt die des neuen Kontakts.
 */
export const haushaltAusFormular = (items: HouseholdItem[], anschriftSelbst: string): SammelMitglied[] =>
  ausRefs(
    items.filter((it) => it.sameHousehold !== false),
    anschriftSelbst,
    false,
  );

/**
 * Anlage, Verbund: bei einer Firma „Firmen-Verbindung zu", bei einer Person
 * „Verbindung zu Unternehmen" (ohne Ausgeschiedene).
 */
export const verbundAusFormular = (
  firmen: FirmaConnItem[] = [],
  unternehmen: PersonFirmaItem[] = [],
): SammelMitglied[] => ausRefs([...firmen, ...unternehmen.filter((u) => !u.ausgeschieden)], "", true);

// ── Ablauf ─────────────────────────────────────────────────────────────

export type SammelErgebnis = {
  name: string;
  /**
   * abgelegt = beim Kontakt erzeugt und heruntergeladen; vorschau = nur
   * heruntergeladen (nicht gespeichert bzw. ohne Schreibrecht).
   */
  ergebnis: "abgelegt" | "vorschau" | "liegt-vor" | "fehler";
  text?: string;
};

export type SammelDienste = Pick<typeof vollmachtService, "anlegen" | "pdf" | "vorschau">;

const liegtVor = (v?: VollmachtDto): boolean => v?.status === "hochgeladen" || v?.status === "unterschrieben";

const fehlerText = (err: unknown): string => (err instanceof Error ? err.message : "unbekannter Fehler");

export const einzelDateiname = (name: string, vorschau: boolean): string =>
  `Kundenerstinformation – ${name}${vorschau ? " – Vorschau" : ""}`
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() + ".pdf";

/**
 * Je Kontakt eine PDF: erzeugen, herunterladen, bei bestehenden Kontakten
 * ablegen. Nacheinander — ein großer Verbund soll den Server nicht mit
 * parallelen Erzeugungen fluten, und die Downloads kommen in Reihenfolge.
 * `vorschauDaten` gilt für Ziele ohne `contactId` (Anlage).
 */
export async function erstinfoJeKontakt(
  ziele: Array<SammelMitglied & { vorschauDaten?: VollmachtVorschauDaten }>,
  dienste: SammelDienste = vollmachtService,
  herunterladen: (b: Blob, name: string) => void = blobHerunterladen,
): Promise<SammelErgebnis[]> {
  const ergebnisse: SammelErgebnis[] = [];
  const vorschau = async (name: string, daten: VollmachtVorschauDaten) => {
    herunterladen(await dienste.vorschau(VOLLMACHT_STANDARD_KEY, daten), einzelDateiname(name, true));
    ergebnisse.push({ name, ergebnis: "vorschau" });
  };
  for (const z of ziele) {
    try {
      if (!z.contactId) {
        await vorschau(z.name, z.vorschauDaten ?? { anzeigeName: z.name, anschrift: z.anschrift, firma: z.firma });
        continue;
      }
      const liste = await dienste.anlegen(z.contactId, [VOLLMACHT_STANDARD_KEY]);
      const v = liste.find((x) => x.art === VOLLMACHT_STANDARD_KEY);
      if (!v) throw new Error("Vollmacht konnte nicht angelegt werden.");
      if (liegtVor(v)) {
        ergebnisse.push({ name: z.name, ergebnis: "liegt-vor" });
        continue;
      }
      herunterladen(await dienste.pdf(z.contactId, v.id), einzelDateiname(z.name, false));
      ergebnisse.push({ name: z.name, ergebnis: "abgelegt" });
    } catch (err) {
      if (z.contactId && err instanceof VollmachtFehler && err.status === 403) {
        // Kein Schreibrecht an diesem Kontakt: herunterladen ja, ablegen nein.
        try {
          await vorschau(z.name, { anzeigeName: z.name, contactId: z.contactId });
        } catch (err2) {
          ergebnisse.push({ name: z.name, ergebnis: "fehler", text: fehlerText(err2) });
        }
      } else {
        ergebnisse.push({ name: z.name, ergebnis: "fehler", text: fehlerText(err) });
      }
    }
  }
  return ergebnisse;
}

/** Eine Zeile Rückmeldung — was abgelegt, nur heruntergeladen, übersprungen oder gescheitert ist. */
export function sammelMeldung(ergebnisse: SammelErgebnis[]): string {
  const namen = (e: SammelErgebnis["ergebnis"]) => ergebnisse.filter((x) => x.ergebnis === e).map((x) => x.name);
  const abgelegt = namen("abgelegt");
  const vorschau = namen("vorschau");
  const vor = namen("liegt-vor");
  const fehler = ergebnisse.filter((x) => x.ergebnis === "fehler");
  const teile: string[] = [];
  if (abgelegt.length) teile.push(`${abgelegt.length} erzeugt, heruntergeladen und beim jeweiligen Kontakt abgelegt`);
  if (vorschau.length) teile.push(`nur heruntergeladen, nicht abgelegt: ${vorschau.join("; ")}`);
  if (vor.length) teile.push(`übersprungen, liegt schon vor: ${vor.join("; ")}`);
  if (fehler.length) teile.push(`fehlgeschlagen: ${fehler.map((f) => `${f.name} (${f.text ?? "Fehler"})`).join("; ")}`);
  return teile.length ? `Kundenerstinformation: ${teile.join(" · ")}.` : "Keine Kundenerstinformation erzeugt.";
}
